import type { CSSProperties, ReactNode } from "react";
import { useTheme } from "../../lib/theme";
import { usePresent } from "./present-context";
import { C, FS, FU, T, TYPE, W, accentInk } from "./tokens";
import { WORKSPACE_ACCENT, type WorkspaceId } from "./workspaces";

/**
 * The signature italic accent word inside a section title (design-system §0.3).
 * Exactly one per opening; its colour is the section's *workspace* hue, pulled
 * from the single `WORKSPACE_ACCENT` source of truth so every section in a
 * workspace shares one accent (Intelligence blue · Demand Signals coral ·
 * Prioritisation purple · GTM amber · Segmentation green).
 *
 *   title={<>Where interest is becoming <AccentWord ws="demand-signals">action</AccentWord></>}
 */
export function AccentWord({ ws, children }: { ws: WorkspaceId; children: ReactNode }) {
  const [theme] = useTheme();
  return <em style={{ fontStyle: "italic", color: accentInk(WORKSPACE_ACCENT[ws], theme === "light") }}>{children}</em>;
}

interface Props {
  n?: string;
  /** Orientation eyebrow. Optional: sections whose top nav already carries the
   *  workspace/section context (Intelligence §1.x) omit it so it isn't said
   *  twice — the numeral + title stand alone (design-system §0.3, Direction A). */
  kicker?: string;
  /** Usually a string; sections on the dark shell may pass a node to carry
   *  the signature italic accent word (design-system v2 §A7.2). */
  title: ReactNode;
  subtitle?: string;
  /** Accepted for call-site compatibility; the Live badge has been removed. */
  live?: boolean;
  /** Renders a muted "Coming soon" pill. */
  comingSoon?: boolean;
  /** Subtitle measure cap. Defaults to 760px (readable line length); pass
   *  "none" to let the subtitle fill the content column and align its right
   *  edge with full-width blocks below it. */
  subtitleMaxWidth?: number | string;
  /** Optional overrides merged onto the kicker eyebrow (e.g. the GTM blue
   *  letter-spaced treatment on dark-shell sections). */
  kickerStyle?: CSSProperties;
  /** Hue of the editorial numeral before the title — the section's workspace
   *  accent. Falls back to the kicker colour, else a muted index tone. */
  accent?: string;
}

export function SectionHeader({
  n,
  kicker,
  title,
  subtitle,
  comingSoon,
  subtitleMaxWidth = "none",
  kickerStyle,
  accent,
}: Props) {
  const [theme] = useTheme();
  // On the present-mode stage the overlay's step label + serif thesis already
  // carry the heading, so the in-body header would just repeat it.
  if (usePresent()) return null;
  // Direction A "editorial numeral": a light serif figure in the workspace hue,
  // set inline as a typographic index before the title — no chip, no box, no
  // trailing rule. Replaces the filled-circle number badge across every section.
  const numColor = accent ? accentInk(accent, theme === "light") : (kickerStyle?.color ?? C.textMd);
  return (
    <div style={{ marginBottom: 32 }}>
      {/* Kicker eyebrow — omitted where a top nav already carries orientation */}
      {kicker ? (
        <div
          style={{
            ...TYPE.eyebrow,
            color: C.textMd,
            textTransform: "uppercase",
            marginBottom: 14,
            ...kickerStyle,
          }}
        >
          {kicker}
        </div>
      ) : null}

      {/* Editorial numeral + title */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
        {n && (
          <span
            style={{
              fontFamily: FS,
              fontStyle: "italic",
              fontWeight: W.light,
              fontSize: 30,
              lineHeight: 1.2,
              color: numColor,
              flexShrink: 0,
            }}
          >
            {n}
          </span>
        )}
        <h2
          style={{
            fontFamily: FS,
            fontSize: 30,
            fontWeight: W.light,
            color: C.textHi,
            margin: 0,
            letterSpacing: 0,
            lineHeight: 1.2,
          }}
        >
          {title}
        </h2>
        {comingSoon && (
          <span
            style={{
              ...TYPE.label,
              color: C.textMd,
              textTransform: "uppercase",
              padding: "3px 10px",
              borderRadius: 4,
              border: `1px solid ${C.border}`,
              background: C.mute,
              whiteSpace: "nowrap",
            }}
          >
            Coming soon
          </span>
        )}
      </div>

      {/* Subtitle */}
      {subtitle ? (
        <p
          style={{
            fontFamily: FU,
            fontSize: T.lead,
            fontWeight: W.body,
            lineHeight: 1.55,
            color: C.textMd,
            maxWidth: subtitleMaxWidth,
            margin: "16px 0 0 0",
          }}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}
